import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

/**
 * Optional curated default graphs in a service's own catalog-info.yaml: a
 * JSON array of `{ title, query }` objects. Used only until someone saves a
 * shared selection in the UI (stored by the datadog-graphs backend plugin);
 * without either, defaults are auto-discovered from the savr.<service>*
 * custom-metric naming convention.
 */
export const DATADOG_GRAPHS_ANNOTATION = 'savr.com/datadog-graphs';

export const DATADOG_UI_URL = 'https://app.datadoghq.eu';

export type GraphDef = {
  title: string;
  query: string;
  // Graph-builder state, kept so the edit dialog reopens with the fields the
  // graph was built from instead of only the raw query. A graph can plot
  // several metrics; they share the aggregation, filter and group-by.
  metrics?: string[];
  aggregation?: string;
  filter?: string;
  groupBy?: string;
  // Optional display names keyed by metric name (or any substring of the
  // series expression), shown in the legend/tooltip instead of the full
  // metric name.
  labels?: Record<string, string>;
  // Optional line colors keyed like labels (ignored for group-by graphs,
  // where every line is the same metric).
  colors?: Record<string, string>;
  // Optional axis labels.
  xLabel?: string;
  yLabel?: string;
  // Optional section name; sections render as tabs (e.g. Errors, Performance).
  section?: string;
  // How to render: timeseries lines (default), a pie of per-series totals,
  // or a ranked top list.
  chartType?: 'line' | 'pie' | 'toplist';
  // Opt-in: external users only ever receive graphs with this set to true
  // (filtered server-side by the datadog-graphs backend).
  externalVisible?: boolean;
};

export type DatadogSeries = {
  pointlist: [number, number | null][];
  display_name?: string;
  expression?: string;
  metric?: string;
  scope?: string;
  unit?: ({ short_name?: string; name?: string } | null)[];
};

/**
 * Zero-config default ping check: an entity annotated with a URL gets a
 * "Ping" health tile without any saved health configuration.
 */
export const PING_URL_ANNOTATION = 'savr.com/ping-url';

export type PingCheck = { type: 'ping'; name: string; url: string };

export type MetricCheck = {
  type: 'metric';
  name: string;
  query: string;
  /** Direction that counts as unhealthy relative to the thresholds. */
  alarmWhen: 'above' | 'below';
  critical: number;
  warning?: number;
  unit?: string;
  /**
   * Evaluation window: the reading is the check's aggregation applied over
   * the last N minutes (e.g. "sum of errors over the last 15 minutes").
   */
  windowMinutes?: number;
  /**
   * What an empty query result means: 'unknown' (default - grey tile,
   * ignored by the org-wide summary), 'healthy' (e.g. an error count where
   * silence is good) or 'unhealthy' (e.g. a heartbeat metric).
   */
  noData?: 'unknown' | 'healthy' | 'unhealthy';
  // Builder state for the edit dialog, mirroring GraphDef.
  metrics?: string[];
  aggregation?: string;
  filter?: string;
};

export const DEFAULT_CHECK_WINDOW_MINUTES = 15;

/**
 * A metric check's reading: its aggregation applied across the window's
 * datapoints (avg by default).
 */
export function windowedValue(
  values: number[],
  aggregation?: string,
): number | undefined {
  if (values.length === 0) {
    return undefined;
  }
  switch (aggregation) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'max':
      return Math.max(...values);
    case 'min':
      return Math.min(...values);
    default:
      return values.reduce((a, b) => a + b, 0) / values.length;
  }
}

export type HealthCheck = PingCheck | MetricCheck;

export type HealthStatus = 'ok' | 'warn' | 'crit' | 'unknown';

export type PingResult = {
  name: string;
  ok: boolean;
  status?: number;
  latencyMs: number;
};

export type SavedGraphs = {
  graphs: GraphDef[] | null;
  health: HealthCheck[] | null;
  updatedBy?: string;
  updatedAt?: string;
};

type Apis = { discoveryApi: DiscoveryApi; fetchApi: FetchApi };

export const buildQuery = (graph: {
  metrics?: string[];
  aggregation?: string;
  filter?: string;
  groupBy?: string;
}) => {
  const filter = graph.filter?.trim() || '*';
  const groupBy = graph.groupBy?.trim();
  return (graph.metrics ?? [])
    .map(
      metric =>
        `${graph.aggregation ?? 'avg'}:${metric}{${filter}}${
          groupBy ? ` by {${groupBy}}` : ''
        }`,
    )
    .join(', ');
};

const entityPath = (entity: Entity) =>
  [entity.kind, entity.metadata.namespace ?? 'default', entity.metadata.name]
    .map(part => encodeURIComponent(part.toLowerCase()))
    .join('/');

async function parseOrThrow(response: Response, fallback: string) {
  const body = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      body?.error ??
        body?.errors?.join('; ') ??
        `${fallback} (${response.status})`,
    );
  }
  return body;
}

/** Timeseries datapoints via the backend's GET-only /datadog/query proxy. */
export async function queryTimeseries(
  { discoveryApi, fetchApi }: Apis,
  query: string,
  fromSeconds: number,
  toSeconds: number,
): Promise<DatadogSeries[]> {
  const base = await discoveryApi.getBaseUrl('proxy');
  const params = new URLSearchParams({
    from: String(fromSeconds),
    to: String(toSeconds),
    query,
  });
  const response = await fetchApi.fetch(`${base}/datadog/query?${params}`);
  const body = await parseOrThrow(response, 'Datadog query failed');
  if (body?.status && body.status !== 'ok') {
    throw new Error(body?.error ?? `Datadog returned "${body.status}"`);
  }
  return (body?.series ?? []) as DatadogSeries[];
}

/** Metric-name search via the /datadog/search proxy (for autocomplete). */
export async function searchMetrics(
  { discoveryApi, fetchApi }: Apis,
  term: string,
): Promise<string[]> {
  const base = await discoveryApi.getBaseUrl('proxy');
  const params = new URLSearchParams({ q: `metrics:${term}` });
  const response = await fetchApi.fetch(`${base}/datadog/search?${params}`);
  const body = await parseOrThrow(response, 'Datadog metric search failed');
  return (body?.results?.metrics ?? []) as string[];
}

/** The shared graph selection stored by the datadog-graphs backend plugin. */
export async function getSavedGraphs(
  { discoveryApi, fetchApi }: Apis,
  entity: Entity,
): Promise<SavedGraphs> {
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(`${base}/graphs/${entityPath(entity)}`);
  return await parseOrThrow(response, 'Failed to load saved graphs');
}

export async function saveGraphs(
  { discoveryApi, fetchApi }: Apis,
  entity: Entity,
  graphs: GraphDef[],
): Promise<void> {
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(
    `${base}/graphs/${entityPath(entity)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ graphs }),
    },
  );
  await parseOrThrow(response, 'Failed to save graphs');
}

export async function saveHealth(
  { discoveryApi, fetchApi }: Apis,
  entity: Entity,
  checks: HealthCheck[],
): Promise<void> {
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(
    `${base}/health/${entityPath(entity)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checks }),
    },
  );
  await parseOrThrow(response, 'Failed to save health checks');
}

/**
 * Org-wide ping status per entity ref ('ok' | 'crit'), cached server-side;
 * only contains entities the caller can read. Powers the health-colored
 * catalog graph.
 */
export async function fetchHealthSummary({
  discoveryApi,
  fetchApi,
}: Apis): Promise<Record<string, string>> {
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(`${base}/health-summary`);
  const body = await parseOrThrow(response, 'Failed to load health summary');
  return (body?.statuses ?? {}) as Record<string, string>;
}

export type UptimeDay = { date: string; total: number; failures: number };

export type UptimeCheck = {
  name: string;
  days: UptimeDay[];
  availability: {
    day: number | null;
    week: number | null;
    month: number | null;
  };
};

/** Per-check daily uptime over the last 30 days (background pinger data). */
export async function fetchUptime(
  apis: Apis,
  entity: Entity,
): Promise<UptimeCheck[]> {
  return await fetchUptimeForRef(apis, stringifyEntityRef(entity));
}

/**
 * The same, addressed by entity ref rather than by a loaded entity.
 *
 * The uptime a caller wants is often not the entity in hand: ping_history is
 * keyed by the deployable's ref, so a capability module's history lives under
 * its parent. Callers that resolve a source ref themselves would otherwise
 * have to fake an Entity to ask for it.
 */
export async function fetchUptimeForRef(
  { discoveryApi, fetchApi }: Apis,
  entityRef: string,
): Promise<UptimeCheck[]> {
  const { kind, namespace, name } = parseEntityRef(entityRef, {
    defaultKind: 'component',
    defaultNamespace: 'default',
  });
  const path = [kind, namespace, name]
    .map(part => encodeURIComponent(part.toLowerCase()))
    .join('/');
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(`${base}/uptime/${path}`);
  const body = await parseOrThrow(response, 'Failed to load uptime history');
  return (body?.checks ?? []) as UptimeCheck[];
}

/** Executes the entity's ping checks server-side and returns their status. */
export async function runPings(
  { discoveryApi, fetchApi }: Apis,
  entity: Entity,
): Promise<PingResult[]> {
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(`${base}/pings/${entityPath(entity)}`);
  const body = await parseOrThrow(response, 'Failed to run ping checks');
  return (body?.results ?? []) as PingResult[];
}

/**
 * The effective health checks when nothing is saved: a single ping from the
 * savr.com/ping-url annotation, if present. Mirrored by the backend's
 * /pings endpoint.
 */
export function defaultHealthChecks(entity: Entity): HealthCheck[] {
  const url = entity.metadata.annotations?.[PING_URL_ANNOTATION];
  return url ? [{ type: 'ping', name: 'Ping', url }] : [];
}

/** Health verdict for a metric check given its latest value. */
export function metricStatus(
  check: MetricCheck,
  value: number | undefined,
): HealthStatus {
  if (value === undefined || Number.isNaN(value)) {
    if (check.noData === 'healthy') {
      return 'ok';
    }
    if (check.noData === 'unhealthy') {
      return 'crit';
    }
    return 'unknown';
  }
  const breaches = (threshold: number) =>
    check.alarmWhen === 'above' ? value >= threshold : value <= threshold;
  if (breaches(check.critical)) {
    return 'crit';
  }
  if (check.warning !== undefined && breaches(check.warning)) {
    return 'warn';
  }
  return 'ok';
}

export async function resetGraphs(
  { discoveryApi, fetchApi }: Apis,
  entity: Entity,
): Promise<void> {
  const base = await discoveryApi.getBaseUrl('datadog-graphs');
  const response = await fetchApi.fetch(
    `${base}/graphs/${entityPath(entity)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error(`Failed to reset graphs (${response.status})`);
  }
}

export function parseAnnotationGraphs(raw: string | undefined): {
  graphs: GraphDef[] | null;
  parseError?: string;
} {
  if (!raw) {
    return { graphs: null };
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return { graphs: null, parseError: 'expected a JSON array' };
    }
    for (const item of parsed) {
      if (
        typeof item?.title !== 'string' ||
        typeof item?.query !== 'string' ||
        !item.title ||
        !item.query
      ) {
        return {
          graphs: null,
          parseError:
            'every entry needs a non-empty string "title" and "query"',
        };
      }
    }
    return { graphs: parsed as GraphDef[] };
  } catch (e) {
    return { graphs: null, parseError: `invalid JSON (${e})` };
  }
}

/**
 * Zero-config defaults from the savr.<service>* custom-metric naming
 * convention: the first candidate prefix with matches wins, capped at six
 * graphs. Returns [] when the service has no custom metrics (yet).
 */
export async function discoverConventionGraphs(
  apis: Apis,
  entity: Entity,
): Promise<GraphDef[]> {
  const name = entity.metadata.name.toLowerCase();
  const candidates = [
    ...new Set([
      `savr.${name.replace(/-/g, '_')}`,
      `savr.${name.replace(/-/g, '.')}`,
      `savr.${name}`,
    ]),
  ];
  for (const prefix of candidates) {
    let metrics: string[];
    try {
      metrics = await searchMetrics(apis, prefix);
    } catch {
      return [];
    }
    const matching = metrics.filter(metric => metric.startsWith(prefix));
    if (matching.length > 0) {
      return matching.slice(0, 6).map(metric => ({
        title: metric.replace(/^savr\./, ''),
        query: buildQuery({ metrics: [metric], aggregation: 'avg' }),
        metrics: [metric],
        aggregation: 'avg',
      }));
    }
  }
  return [];
}
